<template>
  <transition name="sidebar" mode="out-in" v-on:after-enter="checkForScrollbar">
    <div v-if="isChargingpointSelected" class="ActionSidebar" :class="{ open: open }">
      <div class="Tabs">
        <div
          class="Tab"
          :class="activeTab === INFO_TAB ? 'active' : null"
          @click="setActiveTab(INFO_TAB)"
        >
          <SvgIcon icon="info-circle-regular" />
        </div>
        <div
          class="Tab"
          :class="activeTab === OPMERKINGEN_TAB ? 'active' : null"
          @click="setActiveTab(OPMERKINGEN_TAB)"
        >
          <SvgIcon icon="comments-solid" />
        </div>
        <div
          class="Tab"
          :class="activeTab === MONITORING_TAB ? 'active' : null"
          @click="setActiveTab(MONITORING_TAB)"
        >
          <SvgIcon icon="chart-bar-regular" />
        </div>

        <div
          class="Tab ActionSidebar__Collapse"
          v-if="isChargingpointSelected"
          @click="toggle"
        >
          <div
          class="ActionSidebar__Collapse__Icon"
          :class="{ rotated: open }"
          >
            <SvgIcon icon="chevron-left-solid"/>
          </div>
        </div>
      </div>

      <div
        class="ActiveTab__Close"
        :class="{hasScrollbar: this.hasScrollbar}"
        @click="close"
      >
        <SvgIcon icon="times-regular" />
      </div>

      <InfoTab v-if="activeTab === INFO_TAB" />
      <MonitoringTab v-if="activeTab === MONITORING_TAB" />
      <OpmerkingenTab v-if="activeTab === OPMERKINGEN_TAB" />
    </div>
  </transition>
</template>

<script>
import SvgIcon from "@/components/common/SvgIcon";
import InfoTab from "@/components/map/sidebar/Tabs/InfoTab";
import MonitoringTab from "@/components/map/sidebar/Tabs/MonitoringTab";
import OpmerkingenTab from "@/components/map/sidebar/Tabs/OpmerkingenTab";
import { mapActions, mapGetters } from "vuex";
import { EventBus } from '@/services/eventbus'
import { ACTION_SIDEBAR_TAB } from "@/valueholders/enums";

export default {
  name: "ActionSidebar",
  components: {OpmerkingenTab, MonitoringTab, InfoTab, SvgIcon},
  data() {
    return {
      open: false,
      hasScrollbar: false
    }
  },
  computed: {
    ...mapGetters('planmode', ['isChargingpointSelected', 'isChargingpointSelected']),
    ...mapGetters('app', {
      activeTab: 'getSelectedActionSidebarTab'
    }),
  },
  watch: {
    isChargingpointSelected() {
      this.toggle()
    },
  },
  created() {
    this.INFO_TAB = ACTION_SIDEBAR_TAB.INFO;
    this.MONITORING_TAB = ACTION_SIDEBAR_TAB.MONITORING;
    this.OPMERKINGEN_TAB = ACTION_SIDEBAR_TAB.OPMERKINGEN;
  },
  beforeDestroy() {
    // Disconnect the event handler from Mapbox
    this.$store.map.off('click', 'chargingpoints', this.handleClickMarkerEvent)
  },
  methods: {
    ...mapActions('app', ['selectTab']),
    toggle() {
      this.open = !this.open;
    },
    close() {
      this.setActiveTab(null);
      /* send signal to close popup and also deselect charging point, which closes the sidebar;
         by that closing is handled in one place (ChargerDetailsPopup & ChargerEditorPopup) */
      EventBus.$emit('deselect-chargingpoint')
    },
    setActiveTab(tabName) {
      this.selectTab({tabName})

      // fix small delay until new tab is rendered
      setTimeout(this.checkForScrollbar, 0)

      if (tabName !== null && this.open === false) {
        this.toggle()
      }
    },
    handleClickMarkerEvent() {
      this.setActiveTab(this.activeTab)
    },
    checkForScrollbar() {
      const div = document.querySelector('.ActiveTab');

      if (div === null) {
        return
      }

      this.hasScrollbar = div.scrollHeight > div.clientHeight
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/sass/config";

$padding: 12px;
$icon-size: 15px;

$hidden-position: 300px;
$bar-position: 260px; // only the tabs are visible
$open-position: 0;

.ActionSidebar {
  flex: 1 1 auto;
  width: 300px;
  margin-bottom: 10px;

  background: white;
  display: flex;
  min-height: 0; // fix so that sidebar content doesn't push TileData out of flexbox container
  transition: transform .25s ease-out;
  transform: translateX($bar-position);

  &.open {
    transform: translateX($open-position);
  }

  &__Collapse {
    margin-top: auto;

    &__Icon {
      display: inline-block;
      width: 10px;
      height: 25px;
      transform: rotate(0);
      transition: .25s transform ease-out;
      cursor: pointer;
      margin-left: 2px;
      color: map_get($theme-colors, "primary");

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .Tabs {
    width: 40px;
    display: flex;
    flex-direction: column;
    padding-top: ($padding / 2);
    border-right: 1px solid $border-color;
    z-index: 1;

    .svg-container {
      display: inline-block;
    }

    .Tab {
      cursor: pointer;
      font-size: $icon-size;
      padding-top: ($padding / 1.5);
      padding-bottom: ($padding / 1.5);
      padding-left: $padding;
      color: $text-muted;

      &:hover {
        background-color: $body-bg;
      }

      &.active {
        color: map_get($theme-colors, "primary");
        position: relative;

        &::after {
          content: "";
          display: inline-block;
          position: absolute;
          right: -3px;
          top: (($icon-size + $padding) / 8);
          width: 3px;
          height: ($icon-size + $padding * 1.2);
          border-radius: 2px;
          border: 2px solid map_get($theme-colors, "primary");
        }
      }
    }
  }

  .ActiveTab {
    flex: 1 1 auto;
    z-index: 2;
    background: white;
    overflow-y: auto;

    &__Eyebrow {
      font-size: .95rem;
      color: $text-muted;

      &__Icon {
        margin-top: -3px;
      }
    }

    &__Header {
      padding: ($padding/1.2) $padding;
      position: relative;
      border-bottom: 1px solid $border-color;

      h4 {
        margin-bottom: 0;
        font-size: 1.3rem;
        margin-top: 5px;
        font-weight: 400;
      }
    }

    &__Close {
      position: absolute;
      z-index: 3;
      top: $padding;
      right: $padding;
      cursor: pointer;

      &.hasScrollbar {
        right: 28px;
      }
    }

    &__Content {
      padding: ($padding/1.2) $padding;
    }

    .Chart {
      &__Header {
        display: flex;
        font-size: 1.15rem;

        &__Icon {
          width: 25px;
          height: 25px;
          display: flex;
          margin-right: 3px;
        }
      }
    }
  }
}

.sidebar-enter {
  transform: translateX($hidden-position) !important;
}
.sidebar-enter-to {
  transform: translateX($bar-position);
}
.sidebar-enter-active,
.sidebar-leave-active {
  transition: transform .25s ease-out;
}
.sidebar-leave-to {
  transform: translateX($hidden-position) !important;
}
</style>